/**
 * Fast FFT implementation using fft.js library
 * For STFT (Short-Time Fourier Transform) analysis
 */

import FFT from 'fft.js'

// Cache FFT instances by size for reuse
const fftCache = new Map<number, FFT>()

function getFFT(size: number): FFT {
  if (!fftCache.has(size)) {
    fftCache.set(size, new FFT(size))
  }
  return fftCache.get(size)!
}

export interface FFTResult {
  magnitudes: Float32Array
  frequencies: number[]
}

/**
 * Compute FFT for a signal frame using fft.js (O(n log n))
 */
export function computeFFT(
  samples: Float32Array,
  sampleRate: number,
  fftSize: number = 2048
): FFTResult {
  const n = fftSize
  const fft = getFFT(n)

  // Prepare input (pad with zeros if needed)
  const input = new Array(n).fill(0)
  for (let i = 0; i < Math.min(samples.length, n); i++) {
    input[i] = samples[i]
  }

  // Output is interleaved [real0, imag0, real1, imag1, ...]
  const output = fft.createComplexArray()
  fft.realTransform(output, input)

  // Calculate magnitudes (only need first half due to symmetry)
  const magnitudes = new Float32Array(n / 2)
  const frequencies: number[] = []

  for (let k = 0; k < n / 2; k++) {
    const real = output[2 * k]
    const imag = output[2 * k + 1]
    magnitudes[k] = Math.sqrt(real * real + imag * imag) / n
    frequencies.push((k * sampleRate) / n)
  }

  return { magnitudes, frequencies }
}

/**
 * Apply Hanning window to a signal frame
 */
export function applyHanningWindow(samples: Float32Array): Float32Array {
  const n = samples.length
  const windowed = new Float32Array(n)

  for (let i = 0; i < n; i++) {
    const window = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)))
    windowed[i] = samples[i] * window
  }

  return windowed
}

/**
 * Compute STFT (Short-Time Fourier Transform)
 * Returns magnitude spectrogram
 */
export function computeSTFT(
  samples: Float32Array,
  sampleRate: number,
  fftSize: number = 2048,
  hopLength: number = 512
): { magnitudes: Float32Array[]; frequencies: number[] } {
  const numFrames = Math.floor((samples.length - fftSize) / hopLength) + 1
  const magnitudes: Float32Array[] = []
  let frequencies: number[] = []

  for (let i = 0; i < numFrames; i++) {
    const start = i * hopLength
    const frame = samples.slice(start, start + fftSize)

    // Apply window and compute FFT
    const windowed = applyHanningWindow(frame)
    const result = computeFFT(windowed, sampleRate, fftSize)

    magnitudes.push(result.magnitudes)
    if (i === 0) {
      frequencies = result.frequencies
    }
  }

  return { magnitudes, frequencies }
}

/**
 * Get frequency bin indices for a frequency range
 */
export function getFrequencyBins(
  frequencies: number[],
  lowFreq: number,
  highFreq: number
): { lowBin: number; highBin: number } {
  let lowBin = 0
  let highBin = frequencies.length - 1

  for (let i = 0; i < frequencies.length; i++) {
    if (frequencies[i] >= lowFreq && lowBin === 0) {
      lowBin = i
    }
    if (frequencies[i] > highFreq) {
      highBin = i - 1
      break
    }
  }

  return { lowBin, highBin }
}

/**
 * Calculate average magnitude in a frequency range
 */
export function averageMagnitudeInRange(
  magnitudes: Float32Array[],
  frequencies: number[],
  lowFreq: number,
  highFreq: number
): number {
  const { lowBin, highBin } = getFrequencyBins(frequencies, lowFreq, highFreq)

  let sum = 0
  let count = 0

  for (const frame of magnitudes) {
    for (let i = lowBin; i <= highBin && i < frame.length; i++) {
      sum += frame[i]
      count++
    }
  }

  return count > 0 ? sum / count : 0
}
